<script lang="ts">
  /**
   * One shared line for "is the Helper going to send, and as whom" — shown at the
   * top of Posts & Comments across both tabs. Driven entirely by the live helper
   * store (server device heartbeat for online/offline, bridge for the actionable
   * wrong-account / signed-out warnings), so it's the single source of presence.
   */
  import { parseTs, timeAgo } from '../admin';
  import { ensureHelper, helper, postingAs } from '../helper.svelte';
  import HelperSetupModal from './HelperSetupModal.svelte';

  let showSetup = $state(false);

  // A device unseen for this long is treated as offline. The worker heartbeats well
  // under a minute, so three minutes is comfortably idle.
  const OFFLINE_AFTER_MS = 3 * 60_000;

  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  $effect(() => {
    void ensureHelper();
  });

  const activeDevices = $derived(helper.devices.filter((d) => !d.revoked_at));
  const paired = $derived(activeDevices.length > 0);
  const lastSeenMs = $derived.by(() => {
    const times = activeDevices
      .map((d) => d.last_seen_at)
      .filter((s): s is string => !!s)
      .map((s) => parseTs(s).getTime());
    return times.length ? Math.max(...times) : null;
  });
  const serverRecent = $derived(paired && lastSeenMs != null && nowMs - lastSeenMs < OFFLINE_AFTER_MS);
  // The Helper was answering in THIS browser and has gone quiet — a deleted/disabled
  // extension. Trust that over a not-yet-stale server heartbeat, so a fresh deletion
  // reads offline in ~a poll window instead of holding "online" for 3 minutes. When
  // the admin is simply open in a different browser the bridge was never here, so
  // sawBridge is false and we fall back to the server signal as before.
  const localHelperGone = $derived(helper.sawBridge && !helper.bridgeFresh);
  const online = $derived(serverRecent && !localHelperGone);
  const postingLabel = $derived.by(() => {
    const a = postingAs();
    return a ? a.name || (a.handle ? '@' + a.handle : null) : null;
  });
  const liveWho = $derived(
    helper.bridge?.liveSkool?.name ||
      (helper.bridge?.liveSkool?.handle ? '@' + helper.bridge.liveSkool.handle : 'another account'),
  );

  // base-content text on the warning tint reads correctly in both themes;
  // text-warning-content is for a SOLID warning fill and is unreadable here.
  const warnCls =
    'flex items-center gap-2 rounded-full border border-warning/40 bg-warning/10 px-3 py-1 text-base-content/80';
</script>

{#if helper.loaded}
  <div class="text-xs">
    {#if !paired}
      <!-- Not set up: replies still queue and can still be drafted — they just can't
           send yet. Offer the install path rather than a dead-end warning. -->
      <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-field border border-warning/40 bg-warning/10 px-3.5 py-2 text-base-content/80">
        <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warning"></span>
        <span>
          {#if helper.state === 'installed-not-paired'}
            <span class="font-medium">Helper installed, not signed in.</span> Replies queue but won't send yet.
          {:else}
            <span class="font-medium">Helper not set up.</span> Replies queue but won't send yet.
          {/if}
        </span>
        <button
          class="ml-1 shrink-0 font-semibold text-primary underline-offset-2 hover:underline"
          onclick={() => (showSetup = true)}
        >{helper.state === 'installed-not-paired' ? 'How to sign in' : 'Set up the Helper'}</button>
      </div>
    {:else if helper.state === 'account-mismatch'}
      <div class={warnCls}>
        <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warning"></span>
        <span><span class="font-medium">Wrong Skool account.</span> This browser is signed in as {liveWho} — switch back to {postingLabel ?? 'the paired account'} or nothing sends.</span>
      </div>
    {:else if helper.state === 'signed-out-of-skool'}
      <div class={warnCls}>
        <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-warning"></span>
        <span><span class="font-medium">Signed out of Skool.</span> Sign back in to skool.com{#if postingLabel} as {postingLabel}{/if} and queued replies resume.</span>
      </div>
    {:else if helper.state === 'skool-unreadable'}
      <!-- Neutral, not warning: there is nothing to act on, and it usually clears by
           itself on the next poll. Saying it at all beats a silent "Helper online"
           over replies that are not moving. -->
      <div class="flex items-center gap-2 text-base-content/70">
        <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-base-content/30"></span>
        <span><span class="font-medium">Can't check your Skool session.</span> Replies stay queued and retry.</span>
      </div>
    {:else if online}
      <div class="flex items-center gap-2 text-base-content/70">
        <!-- Calm steady dot with a soft halo — no ping. -->
        <span class="relative flex h-2 w-2 shrink-0">
          <span class="absolute inline-flex h-full w-full rounded-full bg-success/25"></span>
          <span class="relative inline-flex h-2 w-2 rounded-full bg-success"></span>
        </span>
        Helper online{#if postingLabel} — posting as <span class="font-medium text-base-content/85">{postingLabel}</span>{/if}
      </div>
    {:else}
      <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-base-content/60">
        <span>
          <span class="inline-block h-2 w-2 shrink-0 rounded-full bg-base-content/35 align-middle"></span>
          <span class="ml-2 font-medium text-base-content/80">Helper offline.</span>
          Open the browser it's signed in to.{#if lastSeenMs != null} <span class="text-base-content/45">Last seen {timeAgo(new Date(lastSeenMs).toISOString(), nowMs)}.</span>{/if}
        </span>
        <button
          class="shrink-0 font-semibold text-primary underline-offset-2 hover:underline"
          onclick={() => (showSetup = true)}
        >Reinstall the Helper</button>
      </div>
    {/if}
  </div>
{/if}

<HelperSetupModal open={showSetup} onClose={() => (showSetup = false)} />
